import type { AxiosInstance } from "axios"
import type { RuntimeConfig } from "#app"
import type { RouteLocationNormalizedLoaded } from "vue-router"
import { CONTACT_INFO } from "~/constants/contact-info"
import { getv4LocaleFromI18nLocale } from "~/scripts/useful-functions"

type PhoneSource = 3 | 4

const getFilteredNumber = (number: any) => {
  if (!number) {
    return number
  }

  return number.replace(/\D/g, "")
}

const getFormattedNumber = (number: any, locale: string) => {
  if (!number) {
    return number
  }

  const cleanNumber = number.replace(/\D/g, "")
  const length = cleanNumber.length

  if (number.trim().includes(" ")) {
    return number
  }

  switch (locale.toLowerCase()) {
    case "en-gb":
      // UK international format: +44XXXXXXXXXX (12 digits total)
      if (length === 12 && cleanNumber.startsWith("44")) {
        const ukDigits = cleanNumber.slice(2)
        return `+44 ${ukDigits.slice(0, 4)} ${ukDigits.slice(4, 7)} ${ukDigits.slice(7, 10)}`
      }
      // UK domestic format: 0XXXXXXXXXX (11 digits)
      if (length === 11 && cleanNumber.startsWith("0")) {
        return `${cleanNumber.slice(0, 5)} ${cleanNumber.slice(5, 8)} ${cleanNumber.slice(8, 11)}`
      }
      break

    case "fr-fr":
      // French international format: +33XXXXXXXXX (11 digits total)
      if (length === 11 && cleanNumber.startsWith("33")) {
        const frenchDigits = cleanNumber.slice(2)
        return `+33 ${frenchDigits.slice(0, 1)} ${frenchDigits.slice(1, 3)} ${frenchDigits.slice(3, 5)} ${frenchDigits.slice(5, 7)} ${frenchDigits.slice(7, 9)}`
      }
      // French domestic format: 0XXXXXXXXX (10 digits)
      if (length === 10 && cleanNumber.startsWith("0")) {
        return `${cleanNumber.slice(0, 2)} ${cleanNumber.slice(2, 4)} ${cleanNumber.slice(4, 6)} ${cleanNumber.slice(6, 8)} ${cleanNumber.slice(8, 10)}`
      }
      break

    case "es-es":
      // Spanish international format: +34XXXXXXXXX (11 digits total)
      if (length === 11 && cleanNumber.startsWith("34")) {
        const spanishDigits = cleanNumber.slice(2)
        return `+34 ${spanishDigits.slice(0, 3)} ${spanishDigits.slice(3, 6)} ${spanishDigits.slice(6, 9)}`
      }
      // Spanish domestic format: [6-9]XXXXXXXX (9 digits)
      if (length === 9 && /^[6-9]/.test(cleanNumber)) {
        return `${cleanNumber.slice(0, 3)} ${cleanNumber.slice(3, 6)} ${cleanNumber.slice(6, 9)}`
      }
      break
  }

  return `${cleanNumber.slice(0, length - (length - 3))} ${cleanNumber.slice(
    length - (length - 3),
    length - 4
  )} ${cleanNumber.slice(length - 4, length)}`
}

const thirtySeconds = 30000

export class PhoneNumberService {
  private axios: AxiosInstance
  private config: RuntimeConfig

  constructor(axios: AxiosInstance, config: RuntimeConfig) {
    this.axios = axios
    this.config = config
  }

  getFormattedPhoneNumberPair(phoneString: string | undefined, locale: string): PhoneNumberPair {
    return {
      base: getFilteredNumber(phoneString),
      baseRaw: getFilteredNumber(phoneString),
      extension: "",
      formatted: getFormattedNumber(phoneString, locale)
    }
  }

  getFormattedPhoneNumberPairFromResponse(response: any, locale: string): PhoneNumberPair | null {
    if (!(response.status === 200 && response.data && response.data.national)) {
      return null
    }

    return {
      base: response.data.full_extended,
      baseRaw: response.data.full,
      extension: response.data.extension,
      formatted: getFormattedNumber(response.data.national, locale)
    }
  }

  async updatePhoneNumber(
    page: Page,
    route: RouteLocationNormalizedLoaded,
    nonce: string,
    locale: string
  ): Promise<PhoneNumberPair> {
    let phoneNumberPair = this.getFormattedPhoneNumberPair(page.customPhoneNumber || "", locale)

    // We don't run the api for
    // page generation
    if (import.meta.server) {
      return phoneNumberPair
    }

    let response = null
    const phoneNumberFromUrl = route.query.phone as string
    if (phoneNumberFromUrl && this.isValidPhoneNumberForLocale(phoneNumberFromUrl, locale)) {
      phoneNumberPair = this.getFormattedPhoneNumberPair(phoneNumberFromUrl, locale)

      try {
        response = await this.sendPersonalisedPhoneNumber(
          phoneNumberPair.base,
          page.uri,
          nonce,
          3,
          locale
        )

        const phoneNumberFromResponse = this.getFormattedPhoneNumberPairFromResponse(
          response,
          locale
        )
        if (phoneNumberFromResponse) {
          phoneNumberPair = phoneNumberFromResponse
          this.pingPersonalisedPhone(phoneNumberPair.baseRaw)
        }
      } catch (e) {
        console.log("There was an error", e)
      }

      return phoneNumberPair
    }

    // Landing page phone number is the second
    if (phoneNumberPair.base) {
      try {
        response = await this.sendPersonalisedPhoneNumber(
          phoneNumberPair.base,
          page.uri,
          nonce,
          4,
          locale
        )

        const phoneNumberFromResponse = this.getFormattedPhoneNumberPairFromResponse(
          response,
          locale
        )
        if (phoneNumberFromResponse) {
          phoneNumberPair = phoneNumberFromResponse
          this.pingPersonalisedPhone(phoneNumberPair.baseRaw)
        }
      } catch (e) {
        console.log("There was an error", e)
      }
      return phoneNumberPair
    }

    // We use contact info as default
    // but we ask for the locale one.
    phoneNumberPair = this.getFormattedPhoneNumberPair(
      CONTACT_INFO.phoneNumbers[locale] ?? CONTACT_INFO.phoneNumbers.en,
      locale
    )

    try {
      response = await this.getPersonalisedPhoneNumber()

      const phoneNumberFromResponse = this.getFormattedPhoneNumberPairFromResponse(response, locale)
      if (phoneNumberFromResponse && phoneNumberFromResponse.baseRaw) {
        phoneNumberPair = phoneNumberFromResponse
        this.pingPersonalisedPhone(phoneNumberPair.baseRaw)
      }
    } catch (e) {
      console.log("There was an error", e)
    }

    return phoneNumberPair
  }

  protected getPersonalisedPhoneNumber(): Promise<any> {
    const personalisedPhoneNumberUrl =
      this.config.v4Host + "/ng/api/v3/actions/get-personalised-number?ignore_previous=true"
    return this.axios.get(personalisedPhoneNumberUrl, {
      headers: {
        "X-Requested-With": "XMLHttpRequest"
      },
      withCredentials: true
    })
  }

  protected sendPersonalisedPhoneNumber(
    personalisedPhoneNumber: string,
    path: string,
    nonce: string,
    phoneSource: PhoneSource,
    locale: string
  ): Promise<any> {
    const personalisedPhoneNumberUrl = this.config.v4Host + "/personalised-number"

    const avLocale = getv4LocaleFromI18nLocale(locale)
    return this.axios.post(
      personalisedPhoneNumberUrl,
      {
        number: personalisedPhoneNumber,
        locale: avLocale,
        landing_path: path,
        landing_source: "cms",
        fw_token: nonce,
        source: phoneSource
      },
      {
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        },
        withCredentials: true
      }
    )
  }

  protected pingPersonalisedPhone(personalisedPhoneNumber: string): void {
    setInterval(() => {
      const personalisedPhoneNumberPing = this.config.v4Host + "/personalised-number/ping"
      this.axios.post(
        personalisedPhoneNumberPing,
        {
          number: personalisedPhoneNumber
        },
        {
          headers: {
            "X-Requested-With": "XMLHttpRequest"
          },
          withCredentials: true
        }
      )
    }, thirtySeconds)
  }

  isValidUkPhoneNumber(phoneNumber: string): boolean {
    const ukPhoneRegex = new RegExp(
      "^(((\\+44\\s?\\d{4}|\\(?0\\d{4}\\)?)\\s?\\d{3}\\s?\\d{3})|((\\+44\\s?\\d{3}|\\(?0\\d{3}\\)?)\\s?\\d{3}\\s?\\d{4})|((\\+44\\s?\\d{2}|\\(?0\\d{2}\\)?)\\s?\\d{4}\\s?\\d{4}))(\\s?\\#(\\d{4}|\\d{3}))?$"
    )
    return ukPhoneRegex.test(phoneNumber)
  }

  isValidSpanishPhoneNumber(phoneNumber: string): boolean {
    const spanishPhoneRegex = new RegExp("^((\\+34\\s?\\d{9})|([89]\\d{8}))$")
    return spanishPhoneRegex.test(phoneNumber.replace(/\s/g, ""))
  }

  isValidFrenchPhoneNumber(phoneNumber: string): boolean {
    const frenchPhoneRegex = new RegExp("^((\\+33\\s?[1-9]\\d{8})|(0[1-9]\\d{8}))$")
    return frenchPhoneRegex.test(phoneNumber.replace(/\s/g, ""))
  }

  isValidPhoneNumberForLocale(phoneNumber: string, locale: string): boolean {
    switch (locale) {
      case "es":
        return this.isValidSpanishPhoneNumber(phoneNumber)
      case "fr":
        return this.isValidFrenchPhoneNumber(phoneNumber)
      default:
        return this.isValidUkPhoneNumber(phoneNumber)
    }
  }
}
